import { useEffect, useId, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { GST_RATES } from '@/lib/tradeCalc';
import { itemOptions, type ItemOption } from '@/api/accountsBooks';
import { newLine, type Line } from './tradeHelpers';
import { SmBtn, XBtn } from './voucherFormParts';
import { dd, inr, r2 } from './voucherFormat';

/** Pieces the Sales and Purchase forms share, as the prototype draws them. */

const UNITS = ['Pcs', 'Nos', 'Set', 'Pair', 'Box', 'Roll', 'Kg', 'Gm', 'Ltr', 'Mtr', 'Sq Mtr', 'Sq Ft', 'Cu Mtr'];

/** Type an item code, name or barcode; pick one of the Item master's matches. */
function ItemPicker({ value, onPick, onText }: { value: string; onPick: (it: ItemOption) => void; onText: (name: string) => void }) {
  const listId = useId();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const opts = useQuery({ queryKey: ['acc-books', 'item-options', debounced], queryFn: () => itemOptions(debounced), enabled: q !== '' });
  const label = (it: ItemOption) => `${it.name} · ${it.code}`;
  return (
    <>
      <Input
        value={value}
        list={listId}
        placeholder="Item code or name"
        className="min-w-[220px]"
        onChange={(e) => {
          const v = e.target.value;
          const hit = (opts.data ?? []).find((it) => label(it) === v);
          if (hit) { onPick(hit); setQ(''); } else { onText(v); setQ(v); }
        }}
      />
      <datalist id={listId}>{(opts.data ?? []).map((it) => <option key={it.id} value={label(it)}>{it.hsn ? `HSN ${it.hsn} · ${it.gst}%` : `${it.gst}%`}</option>)}</datalist>
    </>
  );
}

/**
 * The Items table: Name of item · HSN · Qty · Rate · per · Disc % · GST · Amount, with the
 * Taxable value, the GST heads, Round Off and the invoice total underneath.
 */
export function ItemsTable({ lines, amounts, setLines, taxRows, ro, total, rateOf }: {
  lines: Line[]; amounts: number[]; setLines: (l: Line[]) => void;
  taxRows: [string, number][]; ro: number; total: number;
  /** The rate an item comes in at when picked (Purchase: its purchase rate). */
  rateOf: (it: ItemOption) => number;
}) {
  const set = (i: number, p: Partial<Line>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const num = (v: string) => r2(Number(v) || 0);
  const taxable = r2(amounts.reduce((s, a) => s + a, 0));
  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full min-w-[860px] whitespace-nowrap text-xs">
          <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
            <tr>
              <th className="px-2 py-1.5">Name of item</th><th className="px-2 py-1.5">HSN</th><th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">Rate</th><th className="px-2 py-1.5">per</th><th className="px-2 py-1.5 text-right">Disc %</th>
              <th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Amount</th><th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.key} className="border-t border-[var(--border-color)]">
                <td className="px-2 py-1.5">
                  <ItemPicker value={l.name}
                    onPick={(it) => set(i, { itemId: it.id, name: it.name, hsn: it.hsn, gst: GST_RATES.includes(it.gst) ? it.gst : l.gst, unit: it.unit || 'Pcs', rate: rateOf(it) || l.rate })}
                    onText={(name) => set(i, { name, itemId: null })} />
                </td>
                <td className="px-2 py-1.5"><Input className="!w-[100px] font-mono" maxLength={8} value={l.hsn} onChange={(e) => set(i, { hsn: e.target.value })} /></td>
                <td className="px-2 py-1.5 text-right"><Input type="number" step="0.01" min={0} className="ml-auto !w-[80px] text-right font-mono" value={l.qty || ''} onChange={(e) => set(i, { qty: num(e.target.value) })} /></td>
                <td className="px-2 py-1.5 text-right"><Input type="number" step="0.01" min={0} className="ml-auto !w-[110px] text-right font-mono" value={l.rate || ''} onChange={(e) => set(i, { rate: num(e.target.value) })} /></td>
                <td className="px-2 py-1.5">
                  <Select className="!w-[96px]" aria-label="Unit" value={l.unit} onChange={(e) => set(i, { unit: e.target.value })}>
                    {(UNITS.includes(l.unit) ? UNITS : [l.unit, ...UNITS]).map((u) => <option key={u}>{u}</option>)}
                  </Select>
                </td>
                <td className="px-2 py-1.5 text-right"><Input type="number" step="0.01" min={0} max={100} className="ml-auto !w-[70px] text-right font-mono" placeholder="0" value={l.disc || ''} onChange={(e) => set(i, { disc: num(e.target.value) })} /></td>
                <td className="px-2 py-1.5 text-right">
                  <Select className="ml-auto !w-[80px]" value={l.gst} onChange={(e) => set(i, { gst: Number(e.target.value) })}>
                    {GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
                  </Select>
                </td>
                <td className="px-2 py-1.5 text-right font-mono">{inr(amounts[i] ?? 0)}</td>
                <td className="px-2 py-1.5">{lines.length > 1 && <XBtn title="Remove item" onClick={() => setLines(lines.filter((_, j) => j !== i))} />}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-[var(--border-color)]"><td colSpan={7} className="px-2 py-1.5 text-right font-bold">Taxable value</td><td className="px-2 py-1.5 text-right font-mono font-bold">{inr(taxable)}</td><td /></tr>
            {taxRows.filter(([, a]) => a).map(([n, a]) => <tr key={n}><td colSpan={7} className="px-2 py-1 text-right italic">{n}</td><td className="px-2 py-1 text-right font-mono">{inr(a)}</td><td /></tr>)}
            {ro !== 0 && <tr><td colSpan={7} className="px-2 py-1 text-right italic">Round Off</td><td className="px-2 py-1 text-right font-mono">{ro < 0 ? '(-)' : ''}{inr(Math.abs(ro))}</td><td /></tr>}
            <tr><td colSpan={7} className="px-2 py-1.5 text-right font-bold">Invoice total</td><td className="px-2 py-1.5 text-right font-mono font-bold">{inr(total)}</td><td /></tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-2"><SmBtn onClick={() => setLines([...lines, newLine()])}>＋ Add item</SmBtn></div>
    </>
  );
}

/** Party Details — a collapsible block of free-text fields (open when any is filled). */
export function PartyDetails({ title, fields, pd, setPd }: {
  title: string; fields: [key: string, label: string, span?: number, type?: string][]; pd: Record<string, string>; setPd: (pd: Record<string, string>) => void;
}) {
  const any = fields.some(([k]) => pd[k]);
  return (
    <details open={any} className="mt-3">
      <summary className="mb-2 cursor-pointer border-b border-[var(--border-color)] pb-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-muted)]">{title}</summary>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {fields.map(([k, label, span, type]) => (
          <label key={k} className={`block ${span ? 'col-span-2' : ''}`}>
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{label}</span>
            <Input type={type ?? 'text'} maxLength={200} value={pd[k] ?? ''} onChange={(e) => setPd({ ...pd, [k]: e.target.value })} />
          </label>
        ))}
      </div>
    </details>
  );
}

/** Bill-wise Details: the one New Ref this invoice raises on the party. */
export function NewRefTable({ name, billDate, due, days, amount, side }: { name: string; billDate: string; due: string; days: number; amount: number; side: 'Dr' | 'Cr' }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
      <table className="w-full whitespace-nowrap text-xs">
        <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
          <tr><th className="px-2 py-1.5">Type of Ref</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Bill date</th><th className="px-2 py-1.5">Due on</th><th className="px-2 py-1.5 text-right">Amount</th><th /></tr>
        </thead>
        <tbody>
          <tr className="border-t border-[var(--border-color)]">
            <td className="px-2 py-1.5">New Ref</td><td className="px-2 py-1.5 font-mono">{name || '—'}</td><td className="px-2 py-1.5">{dd(billDate)}</td>
            <td className="px-2 py-1.5">{dd(due)} <span className="text-[var(--text-muted)]">({days} days)</span></td>
            <td className="px-2 py-1.5 text-right font-mono">{inr(amount)}</td><td className="px-2 py-1.5 text-[var(--text-muted)]">{side}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{children}</div>;
}

